import { ChevronLeftIcon } from "@heroicons/react/20/solid";
import { useGlobal } from "@via/hooks/useGlobal.tsx";
import { KEYS, PROTECTED, ROUTES } from "@via/utils/constants.tsx";
import { type JSX, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import "./breadcrumbs.css";

export type BreadcrumbsProps = BreadCrumbsInterface[];

export interface BreadCrumbsInterface {
  label: string;
  path: string;
}

const empty: BreadCrumbsInterface = {
  label: "Home",
  path: "/activity",
};

// We do a string comparison to certain keys to limit the depth of the breadcrumbs generation
const isDepthLimitReached = (label: string): boolean =>
  PROTECTED.some(key => label === key);

export default function Breadcrumbs(): JSX.Element {
  const [crumbs, setCrumbs] = useState<BreadCrumbsInterface[]>([empty]);
  const isLastItem = (i: number): boolean => i === crumbs.length - 1;
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const split = pathname.split("/").filter(item => item !== "");
  const { selectedProject, selectedOrganization } = useGlobal();

  useEffect((): any => {
    let url = "";
    let label = "";
    const assembled: BreadCrumbsInterface[] = [];

    // If we are on a path like `/runs/:id` and `selectedProject` exists, inject the project breadcrumb
    if (split[0] === KEYS.RUNS && selectedProject?.id) {
      assembled.unshift(
        {
          label: "/ projects",
          path: ROUTES.PROJECTS,
        },
        {
          label: `/ ${selectedProject.name}`,
          path: `${ROUTES.PROJECTS}/${selectedProject.id}`,
        }
      );
    }
    for (const [index, item] of split.entries()) {
      label = item.replace(/-/g, " ");
      const previous = split[index - 1];

      if (previous === KEYS.PROJECTS) {
        label = selectedProject?.name ?? "";
      }

      if (isDepthLimitReached(previous)) {
        break;
      }
      // Replace UUID with organization name if applicable
      if (item.match(/^[0-9a-fA-F-]{36}$/) && selectedOrganization) {
        label = selectedOrganization?.name; // Replace UUID with organization name
      }

      // Maintain the last existing path if assembled breadcrumbs are already present
      if (assembled.length > 0) {
        url = assembled[assembled.length - 1].path; // Get the last existing path
      }

      const crumb = { label: `/ ${label}`, path: (url += `/${item}`) };
      assembled.push(crumb);
    }

    assembled.unshift(empty);
    setCrumbs(assembled);
  }, [pathname, selectedProject, selectedOrganization]);

  return (
    <div className="flex items-start gap-2 pl-4">
      <nav className="sm:hidden" aria-label="Back">
        <span
          onClick={() => {
            navigate(-1);
          }}
          className="flex cursor-pointer items-center text-sm font-semibold text-gray-500 hover:text-gray-700">
          <ChevronLeftIcon
            className="-ml-1 mr-1 h-5 w-5 flex-shrink-0 text-gray-400"
            aria-hidden="true"
          />
          Back
        </span>
      </nav>
      <nav className="hidden sm:flex" aria-label="Breadcrumb">
        <ol role="list" className="flex items-center space-x-4">
          <div className="flex capitalize">
            {crumbs.map((crumb, i) => {
              return !isLastItem(i) ? (
                <li key={i}>
                  <Link
                    to={crumb.path}
                    className="pr-1 text-sm font-semibold text-gray-500 hover:text-gray-700">
                    {crumb.label}
                  </Link>
                  {/* Keep this separate so it isn't a link */}
                </li>
              ) : (
                <li key={i}>
                  <Link
                    to={crumb.path}
                    className="text-sm font-semibold text-indigo-600">
                    {crumb.label}
                  </Link>
                </li>
              );
            })}
          </div>
        </ol>
      </nav>
    </div>
  );
}
